/**
 * Structured, isomorphic activity log.
 *
 * Every entry answers: what happened (scope + event), when (ts), how long it
 * took (durationMs), and in what context (correlationId, uid, path...).
 *
 * The core is transport-free — it just fans records out to registered sinks:
 *   - console sink        : always on, both runtimes
 *   - file sink           : server only, installed by src/server.ts
 *   - browser sink        : browser only, ships batches to POST /api/log
 *
 * Keeping node:fs out of this module is deliberate; it must stay importable
 * from client code.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

const LEVEL_WEIGHT: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 };

/** Coarse area of the system a record belongs to. */
export type LogScope =
  | "app" // lifecycle: boot, hydrate, route change
  | "http" // inbound requests handled by this server
  | "api" // outbound calls to the HealNova backend
  | "auth" // Firebase sign-in / sign-up / sign-out
  | "call" // Zego video sessions
  | "copilot" // AI Clinical Copilot: transcript, extraction, prescription flow
  | "run"; // npm script executions (written by scripts/run-logged.mjs)

export interface LogRecord {
  /** ISO-8601 with milliseconds, always UTC. */
  ts: string;
  level: LogLevel;
  scope: LogScope;
  /** Dotted action name, e.g. "request.complete", "signin.failed". */
  event: string;
  runtime: "server" | "browser";
  msg?: string;
  durationMs?: number;
  /** Ties a browser action to the server requests it caused. */
  correlationId?: string;
  [key: string]: unknown;
}

export type LogSink = (record: LogRecord) => void;

const isBrowser = typeof window !== "undefined";
const RUNTIME: LogRecord["runtime"] = isBrowser ? "browser" : "server";

function readEnv(name: string): string | undefined {
  // import.meta.env is inlined at build time; process.env only exists server-side.
  const fromVite = (import.meta.env as Record<string, unknown> | undefined)?.[`VITE_${name}`];
  if (typeof fromVite === "string" && fromVite) return fromVite;
  if (typeof process !== "undefined" && process.env) {
    return process.env[name] ?? process.env[`VITE_${name}`];
  }
  return undefined;
}

const configuredLevel = (readEnv("LOG_LEVEL") ?? "").toLowerCase();
const threshold: LogLevel =
  configuredLevel in LEVEL_WEIGHT
    ? (configuredLevel as LogLevel)
    : import.meta.env.DEV
      ? "debug"
      : "info";

// ---------------------------------------------------------------------------
// Redaction — this is a healthcare app; credentials and PHI must not hit disk.
// ---------------------------------------------------------------------------

const SENSITIVE_KEY = /pass(word)?|token|secret|authorization|api[-_]?key|credential|cookie|otp/i;
const MAX_STRING = 512;
const MAX_DEPTH = 4;

/** True if a field name looks like it carries a credential. */
export function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY.test(key);
}

export function redact(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === "string") {
    return value.length > MAX_STRING
      ? `${value.slice(0, MAX_STRING)}…[+${value.length - MAX_STRING}]`
      : value;
  }
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  if (depth >= MAX_DEPTH) return "[depth-limit]";
  if (Array.isArray(value)) return value.slice(0, 50).map((v) => redact(v, depth + 1));
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEY.test(k) ? "[redacted]" : redact(v, depth + 1);
    }
    return out;
  }
  return String(value);
}

/** Masks an email down to a shape that is still useful for tracing. */
export function maskEmail(email: string | null | undefined): string | undefined {
  if (!email) return undefined;
  const [name, domain] = email.split("@");
  if (!domain || !name) return "[redacted]";
  return `${name.slice(0, 2)}***@${domain}`;
}

// ---------------------------------------------------------------------------
// Sinks
// ---------------------------------------------------------------------------

const sinks = new Set<LogSink>();

export function addSink(sink: LogSink): () => void {
  sinks.add(sink);
  return () => sinks.delete(sink);
}

const CONSOLE_METHOD: Record<LogLevel, "debug" | "info" | "warn" | "error"> = {
  debug: "debug",
  info: "info",
  warn: "warn",
  error: "error",
};

/** `12:04:31.882 INFO  http  request.complete  GET /login 200 (14ms)` */
export function formatRecord(record: LogRecord): string {
  const { ts, level, scope, event, msg, durationMs, runtime, correlationId, ...rest } = record;
  const time = ts.slice(11, 23);
  const head = `${time} ${level.toUpperCase().padEnd(5)} ${scope.padEnd(5)} ${event}`;
  const detail = Object.entries(rest)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`)
    .join(" ");
  const took = durationMs !== undefined ? ` (${durationMs}ms)` : "";
  return [head, msg, detail, took].filter(Boolean).join(" ").trimEnd();
}

const consoleSink: LogSink = (record) => {
  // error-capture.ts wraps console.error; routing through console keeps every
  // record visible in the terminal and the browser devtools alike.
  console[CONSOLE_METHOD[record.level]](formatRecord(record));
};

addSink(consoleSink);

// ---------------------------------------------------------------------------
// Emit
// ---------------------------------------------------------------------------

/** Correlates a browser action with the server work it triggers. */
let ambientCorrelationId: string | undefined;

export function setCorrelationId(id: string | undefined): void {
  ambientCorrelationId = id;
}

export function newCorrelationId(): string {
  const c = globalThis.crypto;
  if (c && "randomUUID" in c) return c.randomUUID().slice(0, 8);
  return Math.floor(Math.random() * 0xffffffff)
    .toString(16)
    .padStart(8, "0");
}

export function emit(record: LogRecord): void {
  if (LEVEL_WEIGHT[record.level] < LEVEL_WEIGHT[threshold]) return;
  for (const sink of sinks) {
    try {
      sink(record);
    } catch {
      // A broken sink must never take down the action being logged.
    }
  }
}

type Context = Record<string, unknown>;

function write(level: LogLevel, scope: LogScope, event: string, context: Context = {}): void {
  const { msg, durationMs, ...rest } = context;
  emit({
    ts: new Date().toISOString(),
    level,
    scope,
    event,
    runtime: RUNTIME,
    ...(ambientCorrelationId ? { correlationId: ambientCorrelationId } : {}),
    ...(typeof msg === "string" ? { msg } : {}),
    ...(typeof durationMs === "number" ? { durationMs } : {}),
    ...(redact(rest) as Context),
  });
}

function nowMs(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

export interface Timer {
  /** Closes the timer and emits the completion record. */
  (context?: Context & { level?: LogLevel; event?: string }): number;
}

export const log = {
  debug: (scope: LogScope, event: string, context?: Context) =>
    write("debug", scope, event, context),
  info: (scope: LogScope, event: string, context?: Context) => write("info", scope, event, context),
  warn: (scope: LogScope, event: string, context?: Context) => write("warn", scope, event, context),
  error: (scope: LogScope, event: string, context?: Context) =>
    write("error", scope, event, context),

  /**
   * Marks the start of an action and returns a closer.
   *
   *   const done = log.start("api", "doctor.fetch", { path });
   *   ...
   *   done({ status: 200 });
   */
  start(scope: LogScope, event: string, context: Context = {}): Timer {
    const t0 = nowMs();
    write("debug", scope, `${event}.start`, context);
    return (closing = {}) => {
      const durationMs = Math.round(nowMs() - t0);
      const { level = "info", event: overrideEvent, ...rest } = closing;
      write(level, scope, overrideEvent ?? `${event}.done`, { ...context, ...rest, durationMs });
      return durationMs;
    };
  },
};

export const LOG_LEVEL = threshold;
